/**
 * ECIES-X25519-AES-GCM encryption for device ID payload.
 * Matches backend encryption format (Node.js crypto / Go).
 */
import { x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha2';
import { randomBytes } from '@noble/hashes/utils';
import { gcm } from '@noble/ciphers/aes';

const ECIES_NONCE_LEN = 12;
const ECIES_HKDF_INFO = 'ECIES-X25519-AES-GCM';

function decodePublicKey(pubKeyBase64) {
  const decoded = Uint8Array.from(
    atob(pubKeyBase64.replace(/\s/g, '')),
    (c) => c.charCodeAt(0)
  );
  return decoded.length >= 32 ? decoded.slice(0, 32) : decoded;
}

export async function encryptWithECIES(plainText, publicKeyBase64) {
  const recipientPubBytes = decodePublicKey(publicKeyBase64);

  const ephemeralPriv = x25519.utils.randomPrivateKey();
  const ephemeralPub = x25519.getPublicKey(ephemeralPriv);

  const sharedSecret = x25519.getSharedSecret(ephemeralPriv, recipientPubBytes);

  const aesKey = hkdf(
    sha256,
    sharedSecret,
    new Uint8Array(0),
    new TextEncoder().encode(ECIES_HKDF_INFO),
    32
  );

  const nonce = randomBytes(ECIES_NONCE_LEN);
  const aes = gcm(aesKey, nonce);
  const plainBytes = new TextEncoder().encode(plainText);
  const ciphertextWithTag = aes.encrypt(plainBytes);

  const out = new Uint8Array(32 + 12 + ciphertextWithTag.length);
  out.set(ephemeralPub, 0);
  out.set(nonce, 32);
  out.set(ciphertextWithTag, 44);

  return btoa(String.fromCharCode.apply(null, out));
}

/**
 * Get encrypted device ID for HTTP headers.
 * Can be called from HTTP layer (pass deviceId) or mixin (deviceId optional).
 */
export async function getEncryptedDeviceIdForHeaders(deviceId) {
  const id =
    deviceId ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('WLdeviceId') : null);
  const publicKey = process.env.VUE_APP_PUBLIC_KEY?.trim();

  if (!id || !publicKey) {
    return '';
  }

  try {
    const payload = `${id}|${Date.now()}`;
    return (await encryptWithECIES(payload, publicKey)) || '';
  } catch (err) {
    console.error('Failed to encrypt device ID:', err);
    return '';
  }
}
